import { getActiveSettings, getSortedRegionsSections } from 'shared/utils';
import { useMemo } from 'react';
import { useNotificationsSettings } from 'features/notifications/hooks/useNotificationsSettings';
import { InlineStatus } from 'components/InlineStatus';
import { Loader } from 'components/Loader';
import { Box, Button } from '@mui/material';
import { DownState } from 'components/DownState';
import AddCountyIcon from 'assets/alerts-add-location.svg?react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NotificationTypes } from 'features/notifications/notifications.constants';
import { RegionEditorCard } from './RegionEditorCard';

type IncidentsSubscriptionsEditorProps = {
  onDownstateClick?: () => void;
};

const footerSx = {
  width: '100%',
  position: 'fixed',
  bottom: 0,
  left: 0,
  padding: 2,
  paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
  backgroundColor: 'background.paper',
  zIndex: 1,
};

export const IncidentsSubscriptionsEditor = (
  props: IncidentsSubscriptionsEditorProps,
): JSX.Element => {
  const { onDownstateClick } = props;
  const { subscribedNotificationSettings } = useNotificationsSettings();
  const { t } = useTranslation();

  const { data, isLoading, isError, isRefetching } =
    subscribedNotificationSettings;

  const regionSettings = data?.data ?? [];

  const activeSettings = getActiveSettings(regionSettings);

  const regionSettingsSections = useMemo(
    () => getSortedRegionsSections(activeSettings, null),
    [activeSettings],
  );

  const isAnyCountySelected = regionSettings.some(
    (rS) => rS.setting !== NotificationTypes.OFF.key,
  );

  if (isLoading || isError || !data) {
    return <InlineStatus status={isLoading ? 'loading' : 'error'} />;
  }

  if (isAnyCountySelected) {
    return (
      <>
        <Box sx={{ paddingX: 2, paddingTop: 4, paddingBottom: 10 }}>
          {regionSettingsSections.map((regionSection) => (
            <RegionEditorCard
              key={regionSection.title}
              title={regionSection.title}
              regionSettings={regionSection.regionSettings}
            />
          ))}

          {isRefetching && <Loader disableBackground />}
        </Box>
        <Box sx={footerSx}>
          <Button
            fullWidth
            size="large"
            component={Link}
            to="/notifications/add_counties"
          >
            {t('notifications.addCounty')}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <DownState
      states={[
        {
          renderImage: (imageProps) => <AddCountyIcon {...imageProps} />,
          title: t('notifications.addCounties.title'),
          description: t('notifications.addCounties.description'),
          Button: (
            <Button
              fullWidth
              size="large"
              component={Link}
              to="/notifications/add_counties"
              onClick={onDownstateClick}
            >
              {t('notifications.addCounty')}
            </Button>
          ),
          containerProps: { height: '100%', px: 2, pt: 6 },
        },
      ]}
    />
  );
};
